import { z } from "zod";
import { permissionsSchema } from "./permissions";

export enum CustomerLicenceOptions {
  NTRWC = "NTRC",
  ISLE_OF_MAN = "ISLE_OF_MAN",
}

export const customerLicenceSchema = z.enum(["NTRC", "ISLE_OF_MAN"]);

export type CustomerLicence = z.infer<typeof customerLicenceSchema>;

export const firebaseTokenSchema = z.object({
  url: z.string(),
  token: z.string(),
  expires: z.string(),
  uid: z.string(),
});

export type FirebaseToken = z.infer<typeof firebaseTokenSchema>;

const guestAttributesSchema = z.object({
  customerLicence: customerLicenceSchema.nullable(),
  zendeskDomain: z.string(),
  zendeskKey: z.string(),
});

const authedAttributesSchema = z.object({
  eventFilters: z.array(z.string()),
  customerLicence: customerLicenceSchema.nullable(),
  zendeskDomain: z.string(),
  zendeskKey: z.string(),
  zendeskMessengerToken: z.string(),
  zendeskDomainTemp: z.string().optional(),
  zendeskKeyTemp: z.string().optional(),
});

export const guestSessionResponseSchema = z.object({
  permissions: permissionsSchema,
  attributes: guestAttributesSchema,
});

export type GuestSessionResponse = z.infer<typeof guestSessionResponseSchema>;

const baseSessionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  token: z.string(),
  firebaseToken: firebaseTokenSchema,
  permissions: permissionsSchema,
  attributes: authedAttributesSchema,
});

// auth endpoint returns expires on POST and expiresAt on GET
// adding both as an xor relationship until that fix is released
export const authedSessionResponseSchema = z.union([
  baseSessionSchema.extend({
    expires: z.string(),
    expiresAt: z.undefined().optional(),
  }),
  baseSessionSchema.extend({
    expiresAt: z.string(),
    expires: z.undefined().optional(),
  }),
]);

export type AuthedSessionResponse = z.infer<typeof authedSessionResponseSchema>;

export type SessionResponse = GuestSessionResponse | AuthedSessionResponse;

export const isAuthedSessionResponse = (
  response: SessionResponse,
): response is AuthedSessionResponse => "userId" in response;

export const signUpProfileSchema = z.object({
  firstName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  dateOfBirth: z.string().optional(),
  phone: z.string().optional(),
  phoneVerificationRequestId: z.string().nullish(),
  street: z.string().optional(),
  streetNumber: z.string().optional(),
  suburb: z.string().optional(),
  state: z.string().optional(),
  postCode: z.string().optional(),
  country: z.string().optional(),
  unit: z.string().optional(),
  depositLimit: z.union([z.number(), z.string()]).optional(),
  depositLimitPeriod: z.union([z.number(), z.string()]).optional(),
  showLimits: z.boolean().optional(),
  terms: z.boolean().optional(),
  promotionCode: z.string().optional(),
  attribution: z.record(z.string(), z.unknown()).optional(),
  nationality: z.string().optional(),
  licence: z.string().nullish(),
  licenceAccepted: z.boolean().optional(),
  hasAffiliateCode: z.boolean().optional(),
  optInToMarketingCommunications: z.boolean().optional(),
});

export type SignUpProfile = z.infer<typeof signUpProfileSchema>;
